import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

export interface AssociateConfigOrdersRequest {
  profileId: number;
  configOrdersIds: number[];
  updatedBy: string;
}

export interface RemoveConfigOrdersRequest {
  profileId: number;
  configOrdersIds: number[];
  updatedBy: string;
}

@Injectable({
  providedIn: 'root'
})
export class ProfileConfigOrdersAssociationsService {
  private readonly baseUrl = environment.microservices?.profileGroup || environment.apiUrl || '/api';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {}

  private getHeaders(): HttpHeaders {
    const token = localStorage.getItem('authToken') || localStorage.getItem('token');
    const headerMap: Record<string, string> = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headerMap['Authorization'] = `Bearer ${token}`;
    }

    return new HttpHeaders(headerMap);
  }

  private buildUrl(path: string): string {
    const [base, baseQuery] = String(this.baseUrl).split('?');
    const normalizedBase = base.replace(/\/+$/, '');
    const normalizedPath = String(path).replace(/^\/+/, '');
    let url = `${normalizedBase}/${normalizedPath}`;

    if (baseQuery) {
      url += `?${baseQuery}`;
    }

    return url;
  }

  /**
   * Associa uma ou mais ConfigOrders a um perfil.
   * POST /AssociateConfigOrdersToProfile
   */
  associateConfigOrdersToProfile(request: AssociateConfigOrdersRequest): Observable<void> {
    return this.http.post<void>(this.buildUrl('AssociateConfigOrdersToProfile'), request, {
      headers: this.getHeaders()
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao associar ConfigOrders ao perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Remove uma ou mais ConfigOrders associadas a um perfil.
   * DELETE /RemoveConfigOrdersFromProfile
   */
  removeConfigOrdersFromProfile(request: RemoveConfigOrdersRequest): Observable<void> {
    return this.http.delete<void>(this.buildUrl('RemoveConfigOrdersFromProfile'), {
      headers: this.getHeaders(),
      body: request
    }).pipe(
      catchError(err => {
        this.errorService.handleHttpError(err, 'Erro ao remover ConfigOrders do perfil');
        return throwError(() => err);
      })
    );
  }

  /**
   * Lista os IDs de ConfigOrders associados a um perfil.
   * GET /GetConfigOrdersByProfile/{profileId}
   */
  getConfigOrdersIdsByProfile(profileId: number): Observable<number[]> {
    return this.http.get<any>(this.buildUrl(`GetConfigOrdersByProfile/${profileId}`), {
      headers: this.getHeaders()
    }).pipe(
      map(response => this.extractConfigOrdersIds(response)),
      catchError(err => {
        this.errorService.handleHttpError(err, `Erro ao buscar ConfigOrders do perfil ${profileId}`);
        return throwError(() => err);
      })
    );
  }

  private extractConfigOrdersIds(response: any): number[] {
    const candidates: any[] = [
      response,
      response?.data,
      response?.data?.data,
      response?.ConfigOrdersIds,
      response?.data?.ConfigOrdersIds,
      response?.data?.data?.ConfigOrdersIds,
      response?.configOrdersIds,
      response?.data?.configOrdersIds,
      response?.data?.data?.configOrdersIds,
      response?.configOrdersIDs,
      response?.data?.configOrdersIDs,
      response?.data?.data?.configOrdersIDs,
      response?.configOrders,
      response?.data?.configOrders,
      response?.data?.data?.configOrders
    ];

    for (const value of candidates) {
      const normalized = this.normalizeNumberArray(value);
      if (normalized.length) {
        return normalized;
      }
    }

    return [];
  }

  private normalizeNumberArray(value: any): number[] {
    if (!value) return [];

    if (Array.isArray(value)) {
      return value
        .map(item => {
          if (typeof item === 'number') return item;
          if (typeof item === 'string') return Number(item);
          return Number(item?.id ?? item?.Id ?? item);
        })
        .filter(n => Number.isFinite(n) && n > 0);
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return [];
      return trimmed
        .split(',')
        .map(part => Number(part.trim()))
        .filter(n => Number.isFinite(n) && n > 0);
    }

    return [];
  }
}
